import { useForm } from "react-hook-form";
import { useState } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@src/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@src/components/ui/radio-group";
import { cn } from "@src/lib/utils";
import { FormControlLabel } from "@mui/material";
import Container from "../shared/Container";
import { useTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/input";
import { axiosInstance } from "@src/utils/axios/AxiosProvider";

const InComeInGelOptions = [
  { value: "1", label: "puKyc.incomeInGelOptions.0-500" },
  { value: "2", label: "puKyc.incomeInGelOptions.500-5000" },
  { value: "3", label: "puKyc.incomeInGelOptions.5000-50000" },
  { value: "4", label: "puKyc.incomeInGelOptions.50000+" },
];

const transactionCategory = [
  { value: "hasGambling", label: "puKyc.transactionCategory.gambling" },
  {
    value: "hasInternatiolalTransactions",
    label: "puKyc.transactionCategory.international",
  },
  { value: "hasTelecomunication", label: "puKyc.transactionCategory.telecom" },
  { value: "hasUtility", label: "puKyc.transactionCategory.utilities" },
  { value: "hasTransport", label: "puKyc.transactionCategory.transport" },
  { value: "hasGeBank", label: "puKyc.transactionCategory.banking" },
  { value: "hasCrypto", label: "puKyc.transactionCategory.crypto" },
  // { value: "hasOther", label: "puKyc.transactionCategory.other" },
];

const nonResidentOptions = [
  {
    value: "isPartnerSelected",
    label: "puKyc.nonResidentOptions.puPartnerCompanies",
  },
  {
    value: "isGeorgiaSelected",
    label: "puKyc.nonResidentOptions.georgia_transactions",
  },
  {
    value: "isNoLocalAlternative",
    label: "puKyc.nonResidentOptions.no_local_product",
  },
  {
    value: "isPayUnicardUsagePlanned",
    label: "puKyc.nonResidentOptions.wantToUsePu",
  },
];

type FormValues = {
  YearlyExpectedTurnoverInGel: string;
  transactionCategory: string[];
  NonResidentWorkPurpose: string[];
  LegalPostalCode: string;
  FactPostalCode: string;
  postalCodesDifferent: boolean;
  HasTransitPurpose: string;
};

const PU_FORM = ({
  userData,
  apiKey,
}: {
  userData: { customerId: string; isResident: string };
  apiKey: string;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      YearlyExpectedTurnoverInGel: undefined,
      transactionCategory: [],
      NonResidentWorkPurpose: [],
      LegalPostalCode: "",
      FactPostalCode: "",
      postalCodesDifferent: false,
      HasTransitPurpose: "",
    },
  });

  const createBooleanMap = (
    allPossibleKeys: string[],
    selectedKeys: string[]
  ) => {
    return allPossibleKeys.reduce((acc, key) => {
      acc[key] = selectedKeys.includes(key);
      return acc;
    }, {} as { [key: string]: boolean });
  };

  const transactionCategoryValues = transactionCategory.map((opt) => opt.value);
  const nonResidentOptionsValues = nonResidentOptions.map((opt) => opt.value);

  const onSubmit = (data: FormValues) => {
    setIsLoading(true);
    const transactionMap = createBooleanMap(
      transactionCategoryValues,
      data.transactionCategory
    );
    const nonResidentMap = createBooleanMap(
      nonResidentOptionsValues,
      data.NonResidentWorkPurpose
    );

    const {
      transactionCategory,
      postalCodesDifferent,
      NonResidentWorkPurpose,
      ...reqDatas
    } = data;

    axiosInstance
      .post(
        "api/customer/register/wb-card",
        {
          ...reqDatas,
          ...transactionMap,
          ...(!userData.isResident ? nonResidentMap : {}),
          CustomerId: userData?.customerId,
          YearlyExpectedTurnoverInGel: Number(data.YearlyExpectedTurnoverInGel),
          HasTransitPurpose: data.HasTransitPurpose === "yes",
        },
        {
          headers: {
            "x-api-key": apiKey,
          },
        }
      )
      .then((res: any) => {
        if (res.status === 200) {
          navigate(`/${i18n.language}/success`, {
            state: {
              title: t("tmc.tmcSuccessTitle"),
              description: t("puKyc.nonResidentOptions.getCard"),
              btn: "show",
            },
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        navigate(`/${i18n.language}/error`, {
          state: {
            title: t("error.title"),
            description: err?.response?.data?.displayText || t("error.desc"),
            errorBtn: "hide",
          },
        });
      });
  };

  return (
    <Container
      className="max-md:min-h-[100vh] max-md:bg-white"
      containerWith="800px"
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-3 max-md:mb-40 mb-[100px]"
        >
          <div className="py-4 gap-4 flex flex-col">
            <p className="text-baseBlack text-xl font-bold leading-[30px]">
              {t("puKyc.titles.incomeInGel")}
            </p>
            <span className="text-xs text-gray-600 font-normal">
              * {t("puKyc.titles.incomeInGelDescription")}
            </span>
          </div>

          <FormField
            control={form.control}
            name="YearlyExpectedTurnoverInGel"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {InComeInGelOptions.map((opt) => (
                      <FormItem
                        className={cn(
                          "flex items-center justify-between gap-1 border-b border-solid border-gray-200",
                          "last:border-b-0 relative"
                        )}
                        key={opt.value}
                      >
                        <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                          {t(opt.label)}
                        </FormLabel>
                        <FormControl className="!mt-0 right-4 absolute">
                          <RadioGroupItem value={opt.value} />
                        </FormControl>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col pt-5">
            <p className="text-baseBlack text-xl font-bold leading-[30px]">
              {t("puKyc.titles.transactionCategory")}
            </p>
          </div>

          <FormField
            control={form.control}
            name="transactionCategory"
            render={({ field }) => (
              <div className="mt-4 flex flex-col gap-3">
                {transactionCategory.map((opt) => (
                  <FormItem
                    className="items-top flex w-full justify-between"
                    key={opt.value}
                  >
                    <FormLabel className="!mt-0 text-sm font-normal text-gray-700 cursor-pointer">
                      {t(opt.label)}
                    </FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value.includes(opt.value)}
                        className="!mt-0"
                        onCheckedChange={(checked) => {
                          if (checked) {
                            field.onChange([...field.value, opt.value]);
                          } else {
                            field.onChange(
                              field.value.filter((v: string) => v !== opt.value)
                            );
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                ))}
              </div>
            )}
          />

          {!userData?.isResident && (
            <>
              <div className="flex flex-col pt-5">
                <p className="text-baseBlack text-xl font-bold leading-[30px]">
                  {t("puKyc.titles.reasonsNonResident")}
                </p>
              </div>
              <FormField
                control={form.control}
                name="NonResidentWorkPurpose"
                rules={{ required: !userData?.isResident }}
                render={({ field }) => (
                  <div className="mt-4 flex flex-col gap-3">
                    {nonResidentOptions.map((opt) => (
                      <FormItem
                        className="items-top flex w-full justify-between"
                        key={opt.value}
                      >
                        <FormLabel className="!mt-0 text-sm font-normal text-gray-700 cursor-pointer">
                          {t(opt.label)}
                        </FormLabel>
                        <FormControl>
                          <Checkbox
                            checked={field.value.includes(opt.value)}
                            className="!mt-0"
                            onCheckedChange={(checked) => {
                              checked
                                ? field.onChange([...field.value, opt.value])
                                : field.onChange(
                                    field.value.filter(
                                      (value) => value !== opt.value
                                    )
                                  );
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    ))}
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="HasTransitPurpose"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-col pt-5">
                      <p className="text-baseBlack text-xl font-bold leading-[30px]">
                        {t("puKyc.titles.transitPurpose")}
                      </p>
                    </div>
                    <FormControl>
                      <RadioGroup
                        className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormItem className="flex items-center justify-between border-b border-solid border-gray-200 last:border-b-0 relative">
                          <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                            {t("puKyc.titles.yes")}
                          </FormLabel>
                          <FormControl className="!mt-0 right-4 absolute">
                            <RadioGroupItem value="yes" />
                          </FormControl>
                        </FormItem>
                        <FormItem className="flex items-center justify-between border-b border-solid border-gray-200 last:border-b-0 relative">
                          <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                            {t("puKyc.titles.no")}
                          </FormLabel>
                          <FormControl className="!mt-0 right-4 absolute">
                            <RadioGroupItem value="no" />
                          </FormControl>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          <div className="flex flex-col pt-5 gap-2">
            <p className="text-baseBlack text-xl font-bold leading-[30px]">
              {t("puKyc.titles.postalCode")}
            </p>

            <FormField
              control={form.control}
              name="LegalPostalCode"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  label={t("puKyc.titles.legalPostalCode")}
                  required
                  className="w-full max-w-xs"
                />
              )}
            />

            <FormField
              control={form.control}
              name="postalCodesDifferent"
              render={({ field }) => (
                <FormControlLabel
                  className="!ml-0"
                  control={
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        checked ? field.onChange(true) : field.onChange(false);
                      }}
                    />
                  }
                  label={
                    <span className="text-xs font-normal text-gray-700 pl-2">
                      {t("puKyc.titles.postalCodesDifferent")}
                    </span>
                  }
                />
              )}
            />

            {form.watch("postalCodesDifferent") && (
              <FormField
                control={form.control}
                name="FactPostalCode"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label={t("puKyc.titles.factPostalCode")}
                    required
                    className="w-full max-w-xs"
                  />
                )}
              />
            )}
          </div>

          <div
            className="w-full self-end rounded-[6px] bg-white py-8
       max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:px-4 max-md:z-50 max-md:shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
          >
            <Button
              className="w-full relative"
              disabled={
                isLoading ||
                !form.watch("YearlyExpectedTurnoverInGel") ||
                form.watch("transactionCategory").length === 0 ||
                !form.watch("LegalPostalCode") ||
                (form.watch("postalCodesDifferent") &&
                  !form.watch("FactPostalCode")) ||
                (!userData?.isResident &&
                  form.watch("NonResidentWorkPurpose").length === 0)
              }
              type="submit"
            >
              {isLoading ? (
                <div className="absolute inset-0 justify-center flex items-center w-full">
                  <Loader className="animate-spin" />
                </div>
              ) : (
                t("puKyc.titles.agree")
              )}
            </Button>
          </div>
        </form>
      </Form>
    </Container>
  );
};

export default PU_FORM;
